import { useMutation, useQuery } from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import type {
  CredentialSchemasResponse,
  CredentialServiceSchema,
  CredentialTestResult,
} from '@/types/credentialSchemas'

export function useCredentialSchemas() {
  return useQuery<CredentialServiceSchema[]>({
    queryKey: ['credential-schemas'],
    queryFn: async () => {
      // Always-on credential-sets route (moved out of the
      // tool_framework-gated integrations router so the Edit Set Keys
      // modal works on enterprise deployments too).
      const data = await apiClient.get<CredentialSchemasResponse>(
        '/api/admin/credential-sets/schemas',
      )
      return data.services
    },
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * User-facing variant: only the services the signed-in user may
 * self-configure (schema-level `user_overridable`). Backed by
 * GET /api/credentials/schemas — plain `credentials:read`, unlike the
 * admin-gated /api/admin/credential-sets/schemas above.
 */
export function useUserCredentialSchemas() {
  return useQuery<CredentialServiceSchema[]>({
    queryKey: ['user-credential-schemas'],
    queryFn: async () => {
      const data = await apiClient.get<CredentialSchemasResponse>(
        '/api/credentials/schemas',
      )
      return data.services
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useTestCredential() {
  return useMutation({
    mutationFn: ({ setId, service }: { setId: string; service: string }) =>
      apiClient.post<CredentialTestResult>(
        `/api/admin/credential-sets/${setId}/entries/${service}/test`,
        {},
      ),
  })
}
